import { notification } from "antd";
import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useCreateProjectMutation,
  useProjectUpdateMutation,
  useProjectUploadAttachmentsMutation,
} from "../../../features/api/apiSlice";
import { Project, ProjectPostValues } from "../../../types/projects";
import { formatDayJSToDateString } from "../../../utils/dates";
import { getErrorMessage } from "../../../utils/errors/errors";
import { FormProjectSchema } from "./FormProjectNew";

export default function useSubmit(isEditMode?: boolean, project?: Project) {
  const navigate = useNavigate();

  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdating }] = useProjectUpdateMutation();
  const [postAttachments, { isLoading: isPostingAttachments }] =
    useProjectUploadAttachmentsMutation();

  const create: SubmitHandler<FormProjectSchema> = useCallback(
    async (values) => {
      try {
        const formattedValues: ProjectPostValues = {
          eventType: "PROJECT",
          name: values.name,
          description: values.description,
          firstMeasure: values.firstMeasure,
          site: Number(values.site),
          localisation: values.localisation,
          dateEvt: formatDayJSToDateString(values.dateEvt),
          accessGivenTo: values.accessGivenTo?.map(Number),
          accessRoleGivenTo: values.accessRoleGivenTo?.map(Number),
        };

        const createdProject = await createProject(formattedValues).unwrap();

        notification.success({
          message: "Projet crÃ©Ã©",
          description: `Le projet ${values.name} a Ã©tÃ© crÃ©Ã© avec succÃ¨s.`,
        });

        const files: undefined | File[] = values?.attachedFiles;

        if (files?.length) {
          await postAttachments({
            projectId: createdProject.id,
            files,
          }).unwrap();
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(`/projects/${createdProject.id}`);
      } catch (error) {
        console.error("Error creating project", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createProject, navigate, postAttachments],
  );

  const update: SubmitHandler<FormProjectSchema> = useCallback(
    async (values) => {
      try {
        if (!project?.id) {
          throw new Error("Pas d'ID de projet");
        }

        const formattedValues: ProjectPostValues = {
          eventType: "PROJECT",
          name: values.name,
          description: values.description,
          firstMeasure: values.firstMeasure,
          site: Number(values.site),
          localisation: values.localisation,
          dateEvt: formatDayJSToDateString(values.dateEvt),
        };

        const updatedProject = await updateProject({
          projectId: project.id,
          values: formattedValues,
        }).unwrap();

        notification.success({
          message: "Projet modifiÃ©",
          description: `Le projet ${values.name} a Ã©tÃ© modifiÃ© avec succÃ¨s.`,
        });

        navigate(`/projects/${updatedProject.id}`);
      } catch (error) {
        console.error("Error updating project", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [updateProject, project?.id, navigate],
  );

  return {
    onSubmit: isEditMode ? update : create,
    isLoading: isCreating || isUpdating || isPostingAttachments,
  };
}
